import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, UserCog, Trash2, Shield, Users } from 'lucide-react';

function UserManagement() {
  const navigate = useNavigate();
  const { user, token, API } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    address: '',
    trade_discount: 0
  });
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    business_name: '',
    trade_discount: 0
  });

  useEffect(() => {
    if (!['admin', 'super_admin', 'company_admin', 'demo'].includes(user?.role)) {
      toast.error('Access denied. Admin only.');
      navigate('/');
      return;
    }
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      role: userToEdit.role || '',
      address: userToEdit.address || '',
      trade_discount: userToEdit.trade_discount || 0
    });
    setEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      setEditDialog(false);
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update user'); toast.error(msg);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Delete user error:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : (error.message || 'Failed to delete user'));
      toast.error(errorMsg);
    }
  };

  const handleRoleChange = async (userId, currentRole, userName) => {
    const newRole = currentRole === 'admin' ? 'staff' : 'admin';
    const action = currentRole === 'admin' ? 'demote' : 'promote';
    
    if (!window.confirm(`${action === 'promote' ? 'Promote' : 'Demote'} "${userName}" to ${newRole}?`)) {
      return;
    }

    try {
      await axios.put(`${API}/users/${userId}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${action}d to ${newRole} successfully`);
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : `Failed to ${action} user`);
      toast.error(msg);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate trade account requires business name
    if (createForm.role === 'trade' && !createForm.business_name) {
      toast.error('Business name required for trade accounts');
      return;
    }
    
    try {
      await axios.post(`${API}/users`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully');
      setCreateDialog(false);
      setCreateForm({ name: '', email: '', password: '', role: 'staff', business_name: '', trade_discount: 0 });
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create user'); toast.error(msg);
    }
  };

  const handleApproveTrade = async (userId, userName) => {
    const discount = prompt(`Enter trade discount percentage for ${userName}:`, '10');
    if (discount === null) return;
    
    const discountNum = parseFloat(discount);
    if (isNaN(discountNum) || discountNum < 0 || discountNum > 100) {
      toast.error('Please enter a valid discount between 0 and 100');
      return;
    }
    
    try {
      await axios.post(`${API}/users/${userId}/approve-trade`, { trade_discount: discountNum }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Trade account approved with ${discountNum}% discount`);
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to approve trade account'); toast.error(msg);
    }
  };

  const handleDenyTrade = async (userId, userName) => {
    const reason = prompt(`Enter reason for denying ${userName}'s trade account (optional):`);
    if (reason === null) return;
    
    try {
      await axios.post(`${API}/users/${userId}/deny-trade`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trade account denied');
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to deny trade account'); toast.error(msg);
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 border-purple-300',
      manager: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      staff: 'bg-blue-100 text-blue-800 border-blue-300',
      workshop: 'bg-orange-100 text-orange-800 border-orange-300',
      client: 'bg-gray-100 text-gray-800 border-gray-300',
      trade: 'bg-amber-100 text-amber-800 border-amber-300'
    };
    
    return (
      <Badge className={`${colors[role] || 'bg-gray-100 text-gray-800 border-gray-300'} border`}>
        {role.toUpperCase()}
      </Badge>
    );
  };
  
  const getTradeStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      denied: 'bg-red-100 text-red-800 border-red-300'
    };
    
    return (
      <Badge className={`${colors[status] || colors.pending} border text-xs`}>
        {status ? status.toUpperCase() : 'PENDING'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 flex items-center justify-center">
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="border-0 shadow-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-purple-600" />
                <div>
                  <CardTitle className="text-3xl">User Management</CardTitle>
                  <CardDescription>Manage users, roles, and permissions</CardDescription>
                </div>
              </div>
              <Button onClick={() => setCreateDialog(true)} className="bg-green-600 hover:bg-green-700">
                <UserCog className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-600 font-medium">Admin Users</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {users.filter(u => u.role === 'admin').length}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 font-medium">Staff Users</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {users.filter(u => u.role === 'staff').length}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 font-medium">Client Users</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.role === 'client').length}
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-600 font-medium">Trade Accounts</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {users.filter(u => u.role === 'trade').length}
                  </p>
                </div>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold text-slate-700">Name</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Email</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Business/Role</th>
                      <th className="text-right p-3 font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {u.id === user.id && (
                              <Badge className="bg-green-100 text-green-800 border-green-300 border text-xs">
                                You
                              </Badge>
                            )}
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-slate-600">{u.email}</td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            {getRoleBadge(u.role)}
                            {u.role === 'trade' && u.business_name && (
                              <span className="text-xs text-gray-600">{u.business_name}</span>
                            )}
                            {u.role === 'trade' && (
                              <div className="flex gap-1 items-center">
                                {getTradeStatusBadge(u.trade_status)}
                                {u.trade_status === 'approved' && u.trade_discount > 0 && (
                                  <span className="text-xs text-amber-600 font-semibold">{u.trade_discount}% discount</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2 justify-end">
                            {u.id !== user.id && (
                              <>
                                {u.role === 'trade' && u.trade_status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleApproveTrade(u.id, u.name)}
                                      className="bg-green-600 hover:bg-green-700 text-xs"
                                    >
                                      ✓ Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleDenyTrade(u.id, u.name)}
                                      className="text-red-600 hover:bg-red-50 border-red-300 text-xs"
                                    >
                                      ✗ Deny
                                    </Button>
                                  </>
                                )}
                                {['admin', 'manager', 'staff', 'workshop'].includes(u.role) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRoleChange(u.id, u.role, u.name)}
                                    className="text-xs"
                                  >
                                    <Shield className="w-3 h-3 mr-1" />
                                    {u.role === 'admin' ? 'Demote to Staff' : 'Promote to Admin'}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEdit(u)}
                                >
                                  <UserCog className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDelete(u.id, u.name)}
                                  className="text-red-600 hover:bg-red-50 border-red-300"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                            {u.id === user.id && (
                              <span className="text-sm text-slate-500 italic">Current user</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information and role</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <select
                id="edit-role"
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
                <option value="workshop">Workshop / Fitter</option>
                <option value="client">Client</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address (Optional)</Label>
              <Input
                id="edit-address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
            </div>
            {editForm.role === 'trade' && (
              <div className="space-y-2">
                <Label htmlFor="edit-trade-discount">Trade Discount (%)</Label>
                <Input
                  id="edit-trade-discount"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={editForm.trade_discount}
                  onChange={(e) => setEditForm({ ...editForm, trade_discount: parseFloat(e.target.value) || 0 })}
                />
                <p className="text-xs text-gray-500">Set the discount percentage for this trade account</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Update User</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new user to the system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Password</Label>
              <Input
                id="create-password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Role</Label>
              <select
                id="create-role"
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
                <option value="workshop">Workshop / Fitter</option>
                <option value="client">Client</option>
                <option value="trade">Trade Account</option>
              </select>
            </div>
            
            {createForm.role === 'trade' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="create-business">Business Name *</Label>
                  <Input
                    id="create-business"
                    value={createForm.business_name}
                    onChange={(e) => setCreateForm({ ...createForm, business_name: e.target.value })}
                    required
                    placeholder="Company Name Ltd"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-discount">Trade Discount (%)</Label>
                  <Input
                    id="create-discount"
                    type="number"
                    min="0"
                    max="100"
                    value={createForm.trade_discount}
                    onChange={(e) => setCreateForm({ ...createForm, trade_discount: parseFloat(e.target.value) })}
                    placeholder="0"
                  />
                </div>
              </>
            )}
            
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">Create User</Button>
              <Button type="button" variant="outline" onClick={() => setCreateDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UserManagement;
